import BaseComponent from '../base-component/base-component'
import { bodyElement } from '../../components/common/body-element'
import PredictiveSearchAPI from '../../api/search-api'
import { getTemplateFirstChildContent, parseHTML } from '../../utils/dom'
import { debounce } from '../../utils/debounce'
import SearchSidebar from './search-sidebar'
import SearchField from '../form/search-input'
import { Nullable } from '../../types/common'

class SearchSidebarFormWrapper extends BaseComponent {
  cache: object
  sidebar: Nullable<SearchSidebar>
  inputComponentSelector: string
  contentResults: string
  footerSelector: string
  itemSelector: string
  results: Nullable<HTMLElement>
  predictiveSearchAPI: PredictiveSearchAPI

  constructor() {
    super()

    this.cache = {}

    this.sidebar = this.closest<SearchSidebar>('#SearchSidebar')

    this.inputComponentSelector = `[data-sidebar-search-input-component]`
    this.contentResults = `[data-sidebar-search-content-results]`
    this.footerSelector = `[data-sidebar-search-footer]`
    this.itemSelector = `[data-sidebar-search-item]`

    if (this.sidebar) {
      this.results = this.sidebar.element.querySelector(this.contentResults)
    }

    this.predictiveSearchAPI = new PredictiveSearchAPI()
  }

  connectedCallback(): void {
    this.addEventListener('input', this.handleInputChange)
  }

  disconnectedCallback(): void {
    this.removeEventListener('input', this.handleInputChange)
  }

  handleInputChange = debounce((): void => {
    if (this.searchQuery) {
      this.renderResults()
    } else {
      this.resetResults()
    }
  }, 500)

  renderResults(): void {
    this.cache[this.searchQuery]
      ? this.renderFromCache(this.searchQuery)
      : this.renderFromUrl(this.searchQuery)
  }

  resetResults(): void {
    if (!this.sidebar) {
      return
    }

    this.updateResultsFromHTML(this.sidebar.defaultStateElement)
  }

  renderFromCache(queryKey: string): void {
    try {
      this.updateResultsFromHTML(this.cache[queryKey])
    } catch (error) {
      if (!bodyElement.notification) {
        return
      }

      bodyElement.notification.show(
        'Error in search component when try update from cache',
        'warning',
      )
    }
  }

  renderFromUrl(queryKey: string): void {
    const sectionId = this.sidebar?.dataset.sectionId || 'search-sidebar'
    const limitParam = 'resources[limit]=999' || ''
    const sectionParam = `section_id=${sectionId}` || ''
    const fieldsParam = this.sidebar?.hasAttribute('enable-extended-search')
      ? 'resources[options][fields]=author,body,product_type,tag,title,variants.barcode,variants.sku,variants.title,vendor'
      : 'resources[options][fields]=title,product_type,variants.title,vendor'

    this.predictiveSearchAPI
      .get({
        searchQuery: this.searchQuery,
        limitParam,
        sectionParam,
        fieldsParam,
      })
      .then(text => {
        const html = parseHTML(text)

        this.cache[queryKey] = html

        this.updateResultsFromHTML(html)
      })
      .catch(() => {
        if (!bodyElement.notification) {
          return
        }

        bodyElement.notification.show('Error in search component', 'warning')
      })
  }

  updateResultsFromHTML(html: Document): void {
    const newModal = html.getElementById('SearchSidebar')

    if (newModal) {
      const template = newModal.querySelector(
        '[data-sidebar-template="SearchSidebar"]',
      )

      const element = getTemplateFirstChildContent(template)

      const newResults = element.querySelector(this.contentResults)

      this.updateResultsHTML(newResults)

      this.updateFooterVisible()
    }
  }

  updateResultsHTML(newResults: HTMLElement): void {
    if (!this.results) {
      return
    }

    const clonedNewResults = newResults.cloneNode(true)

    this.results.replaceChildren(...clonedNewResults.childNodes)
  }

  updateFooterVisible(): void {
    if (this.sidebar) {
      const footer = this.sidebar.element.querySelector(this.footerSelector)
      const renderedItems = this.sidebar.element.querySelectorAll(
        this.itemSelector,
      )

      if (footer) {
        footer.classList.toggle('hidden', renderedItems.length === 0)
      }
    }
  }

  get searchQuery(): string {
    if (!this.inputComponent) {
      return ''
    }

    return this.inputComponent.value.trim()
  }

  get inputComponent(): Nullable<SearchField> {
    if (!this.sidebar) {
      return null
    }

    return this.sidebar.querySelector(this.inputComponentSelector)
  }
}

export default SearchSidebarFormWrapper
